// Guardian edits an existing chore (name, description, zone, reward amount).
// Chore type and child assignment are immutable after creation.
// Editing the reward does NOT retroactively change completed history.

import { type AppError, type ChoreEntry, choreUpdate } from '@chorepop/core/client';
import { type Component, Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  chore: ChoreEntry;
  childDisplayName: string;
  currencyCode: string;
  onSaved: (updated: ChoreEntry) => void;
  onCancel: () => void;
};

export const EditChoreScreen: Component<Props> = (props) => {
  const toRewardString = (minor: number) => (minor === 0 ? '' : (minor / 100).toFixed(2));

  const [name, setName] = createSignal(props.chore.choreName);
  const [description, setDescription] = createSignal(props.chore.choreDescription ?? '');
  const [zoneName, setZoneName] = createSignal(props.chore.zoneName ?? '');
  const [rewardInput, setRewardInput] = createSignal(toRewardString(props.chore.rewardMinorUnits));
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const parseRewardMinorUnits = (): number | null => {
    const raw = rewardInput().trim();
    if (raw === '') return 0;
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
    const major = Number.parseFloat(raw);
    if (!Number.isFinite(major) || major < 0) return null;
    return Math.round(major * 100);
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    if (name().trim() === '') {
      setErrorMessage('Chore name is required.');
      return;
    }

    const reward = parseRewardMinorUnits();
    if (reward === null) {
      setErrorMessage('Enter a valid reward amount (e.g. 0, 1, or 2.50).');
      return;
    }

    setSubmitting(true);
    try {
      const trimmedDesc = description().trim();
      const trimmedZone = zoneName().trim();
      const result = await choreUpdate({
        sessionId: props.sessionId,
        choreId: props.chore.choreId,
        choreName: name().trim(),
        choreDescription: trimmedDesc === '' ? null : trimmedDesc,
        zoneName: trimmedZone === '' ? null : trimmedZone,
        rewardMinorUnits: reward,
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      props.onSaved(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  const currencySymbol = (() => {
    try {
      return (0)
        .toLocaleString(undefined, {
          style: 'currency',
          currency: props.currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim();
    } catch {
      return props.currencyCode;
    }
  })();

  return (
    <ActionScreen
      eyebrow={`For ${props.childDisplayName}`}
      title="Edit chore"
      onBack={props.onCancel}
      footer={
        <button
          type="submit"
          form="edit-chore-form"
          class="primary action-screen-primary"
          disabled={submitting()}
          data-testid="edit-chore-submit"
        >
          {submitting() ? 'Saving…' : 'Save changes'}
        </button>
      }
    >
      <p class="action-screen-intro">
        For {props.childDisplayName}. Changing the reward only affects future completions --
        existing history is unchanged.
      </p>

      <form id="edit-chore-form" onSubmit={handleSubmit} novalidate class="action-form">
        <div class="field">
          <label class="field-label" for="edit-chore-name">
            Chore name
          </label>
          <input
            id="edit-chore-name"
            type="text"
            class="field-input"
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            disabled={submitting()}
            maxlength={120}
            required
            data-testid="edit-chore-name"
          />
        </div>

        <div class="field">
          <label class="field-label" for="edit-chore-description">
            Description <span class="field-optional">(optional)</span>
          </label>
          <textarea
            id="edit-chore-description"
            class="field-input"
            value={description()}
            onInput={(e) => setDescription(e.currentTarget.value)}
            disabled={submitting()}
            rows={3}
            maxlength={500}
            data-testid="edit-chore-description"
          />
        </div>

        <Show when={props.chore.choreTypeCode === 'zone'}>
          <div class="field">
            <label class="field-label" for="edit-chore-zone">
              Zone <span class="field-optional">(optional)</span>
            </label>
            <input
              id="edit-chore-zone"
              type="text"
              class="field-input"
              value={zoneName()}
              onInput={(e) => setZoneName(e.currentTarget.value)}
              disabled={submitting()}
              maxlength={80}
              data-testid="edit-chore-zone"
            />
          </div>
        </Show>

        <div class="field">
          <label class="field-label" for="edit-chore-reward">
            Reward ({currencySymbol})
          </label>
          <input
            id="edit-chore-reward"
            type="text"
            inputmode="decimal"
            class="field-input"
            value={rewardInput()}
            onInput={(e) => setRewardInput(e.currentTarget.value)}
            disabled={submitting()}
            placeholder="0.00"
            data-testid="edit-chore-reward"
          />
        </div>

        <Show when={errorMessage()}>
          {(m) => (
            <div class="form-error" role="alert">
              {m()}
            </div>
          )}
        </Show>
      </form>
    </ActionScreen>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
    case 'not_found':
    case 'conflict':
      return error.message;
    case 'unauthorized':
      return error.message.includes('session')
        ? 'Your session has expired. Please sign in again.'
        : error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
